import * as React from 'react';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { FormField } from '@ooh/ui/components/form-field';
import { Input } from '@ooh/ui/components/input';
import { Separator } from '@ooh/ui/components/separator';
import type { FormValues } from '../../../../lib/media-unit-form-schema';

interface CostsSectionProps {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  t: TFunction<'inventory'>;
  subtitle?: string;
  children?: React.ReactNode;
  /**
   * False when the caller holds cost.view but lacks the wildcard
   * cost.view_rent grant — disables the rent input with a permission
   * explanation instead of accepting an edit that gets silently stripped
   * on save (field-visibility, CU-86cbgnt3z). Every other cost/price
   * input is unaffected. Defaults to true: only the edit form (where a
   * unit's existing rent can be overwritten) gates this — the create form
   * has no stored rent to protect and is unaffected by this change.
   */
  canEditRent?: boolean;
  /**
   * False when the caller lacks cost.view entirely — disables every
   * non-rent internal-cost input with a shared permission explanation
   * (field-visibility, CU-86cbgv42n). Client-price inputs are never gated
   * by cost.view and stay enabled regardless. Defaults to true for the
   * same create-form-is-unaffected reason as canEditRent above.
   */
  canEditCosts?: boolean;
}

type MoneyFieldName =
  | 'rent'
  | 'exposureCost'
  | 'productionCost'
  | 'installCost'
  | 'dismantlingCost'
  | 'additionalCosts'
  | 'catalogPrice'
  | 'discountedPrice'
  | 'lastMinutePrice'
  | 'starterPrice'
  | 'clientProduction'
  | 'clientInstallation'
  | 'clientDismantling'
  | 'clientAdditionalFees';

const COST_FIELDS = [
  'rent',
  'exposureCost',
  'productionCost',
  'installCost',
  'dismantlingCost',
  'additionalCosts',
] as const satisfies readonly MoneyFieldName[];

const PRICE_FIELDS = [
  'catalogPrice',
  'discountedPrice',
  'lastMinutePrice',
  'starterPrice',
  'clientProduction',
  'clientInstallation',
  'clientDismantling',
  'clientAdditionalFees',
] as const satisfies readonly MoneyFieldName[];

function MoneyInputField({
  name,
  register,
  errors,
  t,
  disabled = false,
  disabledHint,
}: {
  name: MoneyFieldName;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  t: TFunction<'inventory'>;
  disabled?: boolean;
  disabledHint?: string;
}) {
  const error = errors[name];
  return (
    <FormField label={t(`form.fields.${name}`)} error={error?.message} testId={`field-${name}`}>
      <DisabledHintTooltip disabled={disabled} hint={disabledHint}>
        <Input
          type="number"
          step="0.01"
          min="0"
          placeholder={t(`form.placeholders.${name}`)}
          error={!!error}
          disabled={disabled}
          data-testid={`input-${name}`}
          {...register(name)}
        />
      </DisabledHintTooltip>
    </FormField>
  );
}

export function CostsSection({
  register,
  errors,
  t,
  subtitle,
  children,
  canEditRent = true,
  canEditCosts = true,
}: CostsSectionProps) {
  const rentDisabledHint = t('form.fields.rentDisabledHint');
  const costsDisabledHint = t('form.fields.costsDisabledHint');
  return (
    <Card data-testid="section-costs">
      <CardHeader className="pb-4">
        <CardTitle className="text-[15px]">{t('form.sections.costs')}</CardTitle>
        {subtitle ? <p className="text-[12px] text-muted-foreground mt-1">{subtitle}</p> : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ── Koszty ─────────────────────────────────────────── */}
        <p className="text-sm font-semibold text-foreground" data-testid="subsection-koszty">
          {t('form.sections.costs')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COST_FIELDS.map((name) => {
            const disabled = name === 'rent' ? !canEditRent : !canEditCosts;
            return (
              <MoneyInputField
                key={name}
                name={name}
                register={register}
                errors={errors}
                t={t}
                disabled={disabled}
                disabledHint={name === 'rent' ? rentDisabledHint : costsDisabledHint}
              />
            );
          })}
        </div>

        <Separator />

        {/* ── Ceny ───────────────────────────────────────────── */}
        <p className="text-sm font-semibold text-foreground" data-testid="subsection-ceny">
          {t('form.sections.prices')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRICE_FIELDS.map((name) => (
            <MoneyInputField key={name} name={name} register={register} errors={errors} t={t} />
          ))}
        </div>

        {children}
      </CardContent>
    </Card>
  );
}
